// apps/client/src/engine/shader/fade.ts (light and look, 2026-10-16) — the occlusion fade's rule, once in GLSL for the world shader and
// once in TypeScript for the CPU (which draws a faded object's translucent ghost, world/outdoor.ts and world/instancer.ts). It replaced
// the see-through circle's screen-space dither: a whole object fades, smoothly, by where it stands, never in a pattern.
//
// Every object that can hide someone carries `aObj` = (x, z, −radius, top) in world units: an upright cylinder (its base centre, its
// radius, the world height of its top). The radius is stored negative so that a mesh without the attribute, which reads the default
// (0, 0, 0, 1), or (1, 1, 1, 1) after a ShaderMaterial's defaults, is never an object. For each resident followed (`uViewers`: chest
// position, w = 1 when on) the object fades when it stands across the sight line from the chest to the camera (`uFadeCam`): near the line
// sideways, reaching above it, and far enough along it toward the camera (not beside you). Anything crowding the camera fades too.
// Fixer 2026-10-18: it counts as hiding the resident when it covers their body on screen, not only the chest's sight line. "Far enough
// along" is how far toward the camera it reaches: its centre's distance along the line plus how far its footprint runs along the strip
// the body's sight lines fill (`strip` either side; eased off smoothly as it leaves the strip, so it never pops), and the sight line's
// height is tested where it comes nearest (halfway from its centre to its near side). So a stand of pines you step into, or a crown whose
// near side covers you, fades instead of being left to the silhouette, while the round tree you face beside you stays whole.
import { tuning } from '@whiskerton/content';
import type * as THREE from 'three';

/** Residents the fade follows: you and the four nearest others. */
export const FADE_VIEWERS = 5;
const F = tuning.light.fade;

/** uViewers[0 … uViewerCount − 1]: the residents followed (packed); uFadeShape = (soft, front start, front full, strip); uFadeNear = (full
 *  fade within, none from). */
export const FADE_GLSL = `uniform vec4 uViewers[ ${FADE_VIEWERS} ]; uniform int uViewerCount; uniform vec3 uFadeCam; uniform vec4 uFadeShape;
uniform vec2 uFadeNear;
float cwFadeBy( vec4 o, vec4 v ) {
  vec2 seg = uFadeCam.xz - v.xz, rel = o.xy - v.xz;
  float len = max( length( seg ), 1e-2 );
  vec2 dir = seg / len;
  float along = dot( rel, dir ), side = abs( rel.x * dir.y - rel.y * dir.x );
  float lateral = length( rel - seg * clamp( along / len, 0.0, 1.0 ) );
  float r = - o.z;
  float reach = r * ( 1.0 - smoothstep( 0.0, r, side - uFadeShape.w ) );
  float sightY = v.y + clamp( along - 0.5 * reach, 0.0, len ) / len * ( uFadeCam.y - v.y );
  return v.w * ( 1.0 - smoothstep( r, r + uFadeShape.x, lateral ) ) * smoothstep( sightY - uFadeShape.x, sightY, o.w )
    * smoothstep( uFadeShape.y, uFadeShape.z, along + reach );
}
float cwFadeOf( vec4 o ) {
  if ( o.z >= 0.0 ) return 0.0;
  float f = 1.0 - smoothstep( uFadeNear.x, uFadeNear.y, length( uFadeCam - vec3( o.x, o.w, o.y ) ) + o.z );
  for ( int i = 0; i < ${FADE_VIEWERS}; i ++ ) {
    if ( i >= uViewerCount ) break;                       // the slots are packed: only the residents followed now cost anything
    f = max( f, cwFadeBy( o, uViewers[ i ] ) );
  }
  return f;
}
`;

/** The ghost's colour pass (fragment): how clear it must be where it lies over a resident it fades for, and its coverage there.
 *  uFadeClear = (radius, soft, chest height, resident height), uFadeClearK = (the window's opacity, how much sooner it fades there);
 *  vCwGhostPos: the fragment (bent
 *  world). Each followed resident is an upright capsule from their feet to their height, as the curved world draws them; the sight ray
 *  from the camera through the fragment is followed to their depth and measured against it. */
export const FADE_CLEAR_GLSL = `uniform vec4 uViewers[ ${FADE_VIEWERS} ]; uniform int uViewerCount; uniform vec3 uFadeCam; uniform vec2 uCamXZ; uniform float uCurve;
uniform float uFadeAlpha; uniform float uFadeEdge; uniform vec4 uFadeClear; uniform vec2 uFadeClearK; varying vec3 vCwGhostPos;
float cwClearOf( vec3 p ) {
  float c = 0.0;
  for ( int i = 0; i < ${FADE_VIEWERS}; i ++ ) {
    if ( i >= uViewerCount ) break;
    vec4 v = uViewers[ i ];
    vec2 bend = v.xz - uCamXZ;
    vec3 feet = vec3( v.x, v.y - uFadeClear.z - dot( bend, bend ) * uCurve, v.z );
    vec2 toV = feet.xz - uFadeCam.xz;
    float depth = length( toV );
    vec3 toP = p - uFadeCam;
    float along = dot( toP.xz, toV ) / max( depth, 1e-4 );
    if ( depth < 1e-3 || along < 1e-3 ) continue;
    vec3 q = uFadeCam + toP * ( depth / along );           // where the fragment's sight ray reaches the resident's depth
    vec2 off = vec2( length( q.xz - feet.xz ), max( 0.0, max( feet.y - q.y, q.y - feet.y - uFadeClear.w ) ) );
    c = max( c, v.w * ( 1.0 - smoothstep( uFadeClear.x, uFadeClear.x + uFadeClear.y, length( off ) ) ) );
  }
  return c;
}
float cwGhostCoverage( float edge, float fade, float clear ) {
  return mix( 1.0, mix( mix( uFadeAlpha, 1.0, edge * edge * uFadeEdge ), uFadeClearK.x, clear ), mix( fade, min( 1.0, fade * uFadeClearK.y ), clear ) );
}
`;

const smoothstep = (a: number, b: number, x: number) => { const t = Math.min(1, Math.max(0, (x - a) / (b - a))); return t * t * (3 - 2 * t); };
/** The shader's cwFadeOf for the object at o[at…at + 3]. `margin` widens every edge (the CPU's ghost test), 0 is the shader's own rule:
 *  a wider radius and strip reach further and come nearer, so the CPU's test is never below the shader's. */
export function fadeAt(o: ArrayLike<number>, at: number, viewers: readonly THREE.Vector4[], cam: THREE.Vector3, margin = 0): number {
  const x = o[at]!, z = o[at + 1]!, r = -o[at + 2]!, top = o[at + 3]!;
  if (!(r > 0)) return 0;
  const near = Math.hypot(cam.x - x, cam.y - top, cam.z - z) - r;
  let f = 1 - smoothstep(F.near[0] + margin, F.near[1] + margin, near);
  const R = r + margin;
  for (const v of viewers) {
    if (!(v.w > 0) || f >= 1) continue;
    const sx = cam.x - v.x, sz = cam.z - v.z, len = Math.max(Math.hypot(sx, sz), 1e-2), dx = sx / len, dz = sz / len;
    const rx = x - v.x, rz = z - v.z, along = rx * dx + rz * dz, side = Math.abs(rx * dz - rz * dx);
    const t = Math.min(1, Math.max(0, along / len)), lateral = Math.hypot(rx - sx * t, rz - sz * t);
    const reach = R * (1 - smoothstep(0, R, side - F.strip - margin));       // how far its footprint runs along the body's sight strip
    const sightY = v.y + (Math.min(len, Math.max(0, along - 0.5 * reach)) / len) * (cam.y - v.y);   // where it comes nearest
    const by = v.w * (1 - smoothstep(R, R + F.soft, lateral)) * smoothstep(sightY - F.soft, sightY, top + margin)
      * smoothstep(Math.max(0, F.front[0] - margin), F.front[1] - margin, along + reach);
    f = Math.max(f, by);
  }
  return f;
}

/** The shader's cwClearOf: 1 where a ghost fragment at `p` (bent world) lies over a followed resident's body, easing to 0 round it. */
export function clearAt(p: THREE.Vector3, viewers: readonly THREE.Vector4[], cam: THREE.Vector3, camXZ: THREE.Vector2, curve: number): number {
  const H = tuning.move.residentHeight;
  let c = 0;
  for (const v of viewers) {
    if (!(v.w > 0)) continue;
    const bx = v.x - camXZ.x, bz = v.z - camXZ.y, fy = v.y - F.chest - (bx * bx + bz * bz) * curve;
    const tx = v.x - cam.x, tz = v.z - cam.z, depth = Math.hypot(tx, tz);
    const px = p.x - cam.x, py = p.y - cam.y, pz = p.z - cam.z, along = (px * tx + pz * tz) / Math.max(depth, 1e-4);
    if (depth < 1e-3 || along < 1e-3) continue;
    const k = depth / along, qx = cam.x + px * k, qy = cam.y + py * k, qz = cam.z + pz * k;
    const off = Math.hypot(Math.hypot(qx - v.x, qz - v.z), Math.max(0, fy - qy, qy - fy - H));
    c = Math.max(c, v.w * (1 - smoothstep(F.clear.radius, F.clear.radius + F.clear.soft, off)));
  }
  return c;
}
/** The shader's cwGhostCoverage: a ghost's opacity where drawn (1 = whole), from how edge on it is seen (0…1), its fade and clearAt. */
export function ghostCoverage(edge: number, fade: number, clear: number): number {
  const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
  return lerp(1, lerp(lerp(F.alpha, 1, edge * edge * F.edge), F.clear.alpha, clear), lerp(fade, Math.min(1, fade * F.clear.boost), clear));
}
